import { useCallback, useRef } from 'react';
import { IconStatusCorrect, useToast } from '@adc/parallax-component-library';

import type { IDN } from '~/api/types';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

import {
  IDNForm,
  type IDNFormRef,
  type IDNFormValues,
} from '../forms/idn/IDNForm';
import { Modal } from '../Modal';

type IDNFormModalProps = {
  testID: string;
  idn?: IDN;
};

export const IDNFormModal = ({ testID, idn }: IDNFormModalProps) => {
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();
  const formRef = useRef<IDNFormRef>(null);
  const isEditing = Boolean(idn?.IDNId);

  const onFormSubmit = useCallback(
    (values: IDNFormValues) => {
      // TODO: Handle form submission logic here
      console.warn(
        `IDN ${isEditing ? 'Edited' : 'Created'} with values:`,
        values,
      );

      closeModal('IDNForm');

      if (isEditing) {
        showToast({
          message: 'IDN profile updated',
          'aria-label': 'IDN profile updated',
          icon: <IconStatusCorrect accentColor="$support.positive.default" />,
        });
      } else {
        openModal('IDNAdded', {
          IDNId: values.id,
          IDNName: values.name,
          testID: 'idn-added-modal',
        });
      }
    },
    [openModal, closeModal, isEditing, showToast],
  );

  return (
    <Modal
      testID={testID}
      title={isEditing ? `Edit IDN - ${idn?.IDN}` : 'Add IDN'}
      hasCloseButton
      size="m"
      primaryAction={{
        label: isEditing ? 'Save Changes' : 'Complete',
        onPress: () => {
          void formRef.current?.submitForm();
        },
      }}
      secondaryAction={{
        label: 'Cancel',
        onPress: () => {
          closeModal('IDNForm');
        },
      }}
      onOpenChange={() => {
        closeModal('IDNForm');
      }}
    >
      <IDNForm
        ref={formRef}
        testID={buildTestId({
          testID,
          suffix: 'form',
        })}
        onSubmit={onFormSubmit}
        defaultValues={
          isEditing
            ? {
                id: idn?.IDNId,
                name: idn?.IDN,
                phone: idn?.phone,
                address: idn?.address.mainLine,
                addressLine2: idn?.address.secondaryLine,
                state: idn?.address.state,
                city: idn?.address.city,
                country: idn?.address.country,
                zipCode: idn?.address.zipCode,
              }
            : undefined
        }
      />
    </Modal>
  );
};
